import { uniqueKey } from '../CalculationBuilder';

const STRATEGIES = [
  { value: 'cost_type_expense', label: 'Destination Cost Type Expense Account' },
  { value: 'configured_account', label: 'Specified GL Account' },
  { value: 'insurance_recovery', label: 'Insurance Recovery' },
  { value: 'accrued_insurance', label: 'Accrued Insurance' },
];

export function emptyAccountingLine() {
  return {
    id: Math.random().toString(36).slice(2, 10),
    side: 'debit',
    amount_key: '',
    strategy: 'configured_account',
    role_key: '',
    role_label: '',
    account_vuid: '',
    job_cost_policy: 'never',
    line_key: '',
    memo: '',
  };
}

export function accountingFromDistribution(distribution) {
  const lines = (distribution?.lines || []).map((line, index) => {
    const account = line.account || {};
    const strategy = account.strategy === 'named_resolver'
      ? (account.resolver || 'insurance_recovery')
      : (account.strategy || 'configured_account');
    return {
      id: Math.random().toString(36).slice(2, 10),
      side: line.side === 'credit' ? 'credit' : 'debit',
      amount_key: line.amount_key || '',
      strategy,
      role_key: account.role_key || '',
      role_label: account.role_label || '',
      account_vuid: account.account_vuid || '',
      job_cost_policy: line.job_cost_policy === 'always' || line.job_cost_policy === 'never' || line.job_cost_policy === 'project_choice'
        ? line.job_cost_policy
        : (line.creates_project_cost ? 'always' : 'never'),
      line_key: line.line_key || `line-${line.line_number || index + 1}`,
      memo: line.memo || '',
    };
  });
  return lines;
}

export function distributionFromAccounting(lines, namedValues) {
  const used = [];
  return {
    lines: lines.map((line, index) => {
      const named = namedValues.find((item) => item.key === line.amount_key);
      const namedResolver = line.strategy === 'insurance_recovery' || line.strategy === 'accrued_insurance';
      let roleKey = line.role_key;
      if (namedResolver) {
        roleKey = line.strategy;
      } else if (line.strategy === 'configured_account') {
        roleKey = roleKey || uniqueKey(line.role_label || `account ${index + 1}`, used);
      }
      if (roleKey) used.push(roleKey);
      const account = namedResolver
        ? {
          strategy: 'named_resolver',
          resolver: line.strategy,
          role_key: roleKey,
          role_label: line.role_label || (line.strategy === 'insurance_recovery' ? 'Insurance Recovery' : 'Accrued Insurance'),
          account_vuid: null,
        }
        : line.strategy === 'cost_type_expense'
          ? {
            strategy: 'cost_type_expense',
            role_key: '',
            role_label: 'Destination Cost Type Expense Account',
            account_vuid: null,
            resolver: null,
          }
          : {
            strategy: 'configured_account',
            role_key: roleKey,
            role_label: line.role_label || roleKey,
            account_vuid: line.account_vuid || null,
            resolver: null,
          };
      return {
        line_number: index + 1,
        side: line.side === 'credit' ? 'credit' : 'debit',
        amount_key: named ? named.key : (line.amount_key || ''),
        account,
        line_key: line.line_key || line.id,
        job_cost_policy: line.job_cost_policy || 'never',
        creates_project_cost: (line.job_cost_policy || 'never') === 'always',
        memo: line.memo || '',
      };
    }),
  };
}

export default function AccountingEditor({ lines, setLines, namedValues, accounts, editing }) {
  const update = (id, patch) => {
    setLines((rows) => rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  return (
    <section className="bg-white rounded-xl shadow-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-semibold text-gray-900">Accounting</h2>
        {editing ? (
          <button type="button" className="btn-claret" onClick={() => setLines((rows) => [...rows, emptyAccountingLine()])}>
            Add line
          </button>
        ) : null}
      </div>
      <p className="text-sm text-gray-600 mb-4">
        How the calculated amount hits the ledger. Debit and credit lines use a calculated value.
        Project destinations and account overrides are set on each project.
      </p>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="text-left text-gray-600">
            <tr>
              <th className="py-2 pr-3">Side</th>
              <th className="py-2 pr-3">Amount</th>
              <th className="py-2 pr-3">Account</th>
              <th className="py-2 pr-3">Project Cost</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <tr key={line.id} className="border-t border-gray-200 align-top">
                <td className="py-3 pr-3">
                  <select
                    className="px-2 py-2 border border-gray-300 rounded-md"
                    value={line.side}
                    disabled={!editing}
                    onChange={(event) => update(line.id, { side: event.target.value })}
                  >
                    <option value="debit">Debit</option>
                    <option value="credit">Credit</option>
                  </select>
                </td>
                <td className="py-3 pr-3">
                  <select
                    className="px-2 py-2 border border-gray-300 rounded-md min-w-[10rem]"
                    value={line.amount_key}
                    disabled={!editing}
                    onChange={(event) => update(line.id, { amount_key: event.target.value })}
                  >
                    <option value="">Choose a calculated value</option>
                    {namedValues.filter((item) => item.key).map((item) => (
                      <option key={item.key} value={item.key}>{item.label || item.key}</option>
                    ))}
                  </select>
                </td>
                <td className="py-3 pr-3">
                  <select
                    className="px-2 py-2 border border-gray-300 rounded-md mb-2 w-full"
                    value={line.strategy}
                    disabled={!editing}
                    onChange={(event) => {
                      const strategy = event.target.value;
                      const patch = { strategy };
                      if (strategy === 'insurance_recovery') {
                        patch.role_key = 'insurance_recovery';
                        patch.role_label = 'Insurance Recovery';
                      }
                      if (strategy === 'accrued_insurance') {
                        patch.role_key = 'accrued_insurance';
                        patch.role_label = 'Accrued Insurance';
                      }
                      update(line.id, patch);
                    }}
                  >
                    {STRATEGIES.map((item) => (
                      <option key={item.value} value={item.value}>{item.label}</option>
                    ))}
                  </select>
                  {line.strategy === 'configured_account' ? (
                    <div className="space-y-2">
                      <input
                        className="px-2 py-2 border border-gray-300 rounded-md w-full"
                        placeholder="Account name, such as Labor Burden Offset"
                        value={line.role_label}
                        disabled={!editing}
                        onChange={(event) => update(line.id, { role_label: event.target.value, role_key: '' })}
                      />
                      <select
                        className="px-2 py-2 border border-gray-300 rounded-md w-full"
                        value={line.account_vuid}
                        disabled={!editing}
                        onChange={(event) => update(line.id, { account_vuid: event.target.value })}
                      >
                        <option value="">No library default</option>
                        {accounts.map((account) => (
                          <option key={account.vuid} value={account.vuid}>
                            {account.account_number} - {account.account_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : null}
                  {line.strategy === 'cost_type_expense' ? (
                    <p className="text-xs text-gray-500">Uses the expense account for the destination cost type on each project, including a project override. Project cost is a separate choice.</p>
                  ) : null}
                  {line.strategy === 'insurance_recovery' || line.strategy === 'accrued_insurance' ? (
                    <p className="text-xs text-gray-500">Uses the company account, then the project GL override. The assignment can override it too.</p>
                  ) : null}
                </td>
                <td className="py-3 pr-3">
                  <select
                    className="px-2 py-2 border border-gray-300 rounded-md"
                    value={line.job_cost_policy || 'never'}
                    disabled={!editing}
                    onChange={(event) => update(line.id, { job_cost_policy: event.target.value })}
                  >
                    <option value="always">Yes</option>
                    <option value="never">No</option>
                    <option value="project_choice">Project decides</option>
                  </select>
                  {line.job_cost_policy === 'always' ? (
                    <p className="text-xs text-gray-500 mt-1">The calculated amount is added to project cost at the destination on each project.</p>
                  ) : null}
                  {line.job_cost_policy === 'never' ? (
                    <p className="text-xs text-gray-500 mt-1">The journal posts. This amount is not added to project cost.</p>
                  ) : null}
                  {line.job_cost_policy === 'project_choice' ? (
                    <p className="text-xs text-gray-500 mt-1">Each project chooses Create Project Cost or Journal Only. The journal account stays the same.</p>
                  ) : null}
                </td>
                <td className="py-3">
                  {editing ? (
                    <button type="button" className="text-sm underline" onClick={() => setLines((rows) => rows.filter((item) => item.id !== line.id))}>
                      Remove
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
            {!lines.length ? (
              <tr>
                <td className="py-3 text-gray-500" colSpan={5}>No accounting lines yet.</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </section>
  );
}
